import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import classNames from "classnames";

import { IconChevronLeft, IconChevronRight, IconX } from "./Icons";

export const GalleryModal = forwardRef(({ images }, ref) => {
  //#region Data
  const [index, setIndex] = useState(-1);
  //#endregion

  //#region Event
  const onPrev = useCallback(() => {
    setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const onNext = useCallback(() => {
    setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const onClose = () => {
    setIndex(-1);
  };
  //#endregion

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onPrev, onNext]);

  useImperativeHandle(ref, () => ({
    open: (index = 0) => {
      setIndex(index);
    },
  }));

  //#region Render
  return (
    <div
      className={classNames(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm transition-all",
        index !== -1
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none",
      )}
      onClick={onClose}
    >
      <button
        className="absolute top-6 right-6 outline-none p-2 text-white hover:bg-white/10 rounded-full transition-colors z-10"
        onClick={onClose}
      >
        <IconX size={32} />
      </button>

      {images.length > 1 && (
        <>
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 outline-none p-3 text-white hover:bg-white/10 rounded-full transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
          >
            <IconChevronLeft size={48} />
          </button>
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 outline-none p-3 text-white hover:bg-white/10 rounded-full transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
          >
            <IconChevronRight size={48} />
          </button>
        </>
      )}

      {images.length > 0 && (
        <div
          className="max-w-7xl max-h-[75vh] p-4 flex flex-col items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[index]?.path}
            alt={`Preview ${index}`}
            className="w-auto max-h-[65vh] object-cover shadow-2xl rounded-lg border border-white/10 select-none"
          />
          <div className="mt-4 text-white text-sm font-medium bg-black/50 px-4 py-2 rounded-full backdrop-blur-md">
            {index + 1} / {images.length} — {images[index]?.original_name}
          </div>
        </div>
      )}

      {images.length > 1 && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 p-3 bg-black/60 backdrop-blur-md rounded-xl max-w-[90vw] overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, idx) => (
            <button
              key={img.__id}
              className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all outline-none ${
                idx === index
                  ? "border-white scale-110 shadow-lg"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
              onClick={() => setIndex(idx)}
            >
              <img
                src={img.path}
                alt={`Thumbnail ${idx}`}
                className="w-full h-full object-cover select-none"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
  //#endregion
});

GalleryModal.displayName = "GalleryModal";
